import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './PrivacyScreen.scss';
import collapseIcon from '../../../../assets/images/icons/minus-terms-and-condition.png';
import expandIcon from '../../../../assets/images/icons/plus-terms-and-condition.png';
import ConsentService, { ConsentConfig } from '../../../../api/services/ConsentService';

const PrivacyScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [consents, setConsents] = useState<ConsentConfig[]>([]);
  const [expandedConsents, setExpandedConsents] = useState<{ [key: string]: boolean }>({});
  const [isPrivacyPolicyExpanded, setIsPrivacyPolicyExpanded] = useState<boolean>(false);
  const [isTermsAndConditionsExpanded, setIsTermsAndConditionsExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConsents = async () => {
      try {
        setLoading(true);
        setError(null);
        const language = i18n.language === 'es' ? 'es' : 'en';
        const response = await ConsentService.getConsents(language);
        setConsents(response.resource);
      } catch (err) {
        console.error('Error fetching consents:', err);
        setError(t('Failed to load consent documents. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    fetchConsents();
  }, [i18n.language, t]);

  const toggleConsent = (consentType: string) => {
    setExpandedConsents((prev) => ({
      ...prev,
      [consentType]: !prev[consentType],
    }));
  };

  const formatContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let listKey = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${listKey++}`} className='consent-list'>
            {currentList.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>,
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Check if line starts with bullet point
      if (trimmedLine.startsWith('•')) {
        const itemText = trimmedLine.substring(1).trim();
        currentList.push(itemText);
      } else if (trimmedLine === '') {
        // Empty line - flush current list and add spacing
        flushList();
        if (elements.length > 0) {
          elements.push(<div key={`space-${index}`} className='consent-paragraph-break' />);
        }
      } else if (trimmedLine) {
        // Regular paragraph text
        flushList();
        elements.push(
          <p key={`p-${index}`} className='consent-paragraph'>
            {trimmedLine}
          </p>,
        );
      }
    });

    // Flush any remaining list items
    flushList();

    return elements;
  };

  if (loading) {
    return (
      <div className='privacy-container'>
        <div className='privacy-content'>
          <p className='loading-text'>{t('Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='privacy-container'>
        <div className='privacy-content'>
          <p className='error-text'>{error}</p>
        </div>
      </div>
    );
  }

  const privacyPolicyContent = `These Terms and Conditions define the obligations of Direct2Care, its authorized agents and me, the service subscriber, and they establish the basic rules of safe and fair use of Direct2Care services. Direct2Care and its authorized agents reserve the right to immediately and without advance notice terminate the service and deny access to individuals who do not abide by the Terms and Conditions.

By using the Direct2Care public Website, the Direct2Care secure Website and the Direct2Care service, I signify my acceptance of the Direct2Care Service Agreement Terms and Conditions. If I do not accept the Direct2Care Service Agreement Terms and Conditions I should not use this service. If Direct2Care changes the Service Agreement Terms and Conditions, they will post those changes prominently. My continued use of the services and Websites following the posting of changes to these terms will mean I accept those changes. Changes to the Service Agreement Terms and Conditions will become effective immediately upon posting on the Direct2Care Websites and shall supersede all prior versions of the Terms and Conditions unless otherwise noted.`;

  return (
    <div className='privacy-container'>
      <div className='privacy-content'>
        {/* Privacy Policy Section */}
        <div className='privacy-row'>
          <button
            onClick={() => setIsPrivacyPolicyExpanded(!isPrivacyPolicyExpanded)}
            className='privacy-policy-button'
            data-testid='privacy-tab-privacy-policy'
          >
            <span className='privacy-row-title'>{t('Privacy Policy')}</span>
            <img
              src={isPrivacyPolicyExpanded ? collapseIcon : expandIcon}
              alt={isPrivacyPolicyExpanded ? 'Collapse' : 'Expand'}
              className='icon'
            />
          </button>
          {isPrivacyPolicyExpanded && (
            <div className='description'>{formatContent(privacyPolicyContent)}</div>
          )}
        </div>
        <div className='border-line' />

        {/* Terms & Conditions Section */}
        <div className='privacy-row'>
          <button
            onClick={() => setIsTermsAndConditionsExpanded(!isTermsAndConditionsExpanded)}
            className='privacy-policy-button'
            data-testid='privacy-tab-terms-and-conditions'
          >
            <span className='privacy-row-title'>{t('Terms & Conditions')}</span>
            <img
              src={isTermsAndConditionsExpanded ? collapseIcon : expandIcon}
              alt={isTermsAndConditionsExpanded ? 'Collapse' : 'Expand'}
              className='icon'
            />
          </button>
        </div>

        {/* Dynamic Consents - Only show when Terms & Conditions is expanded */}
        {isTermsAndConditionsExpanded &&
          consents.map((consent) => {
            const consentType = consent.config_value.consent_type;
            const isExpanded = expandedConsents[consentType] || false;

            return (
              <React.Fragment key={consentType}>
                <div className='border-line' />
                <div className='privacy-row'>
                  <button
                    onClick={() => toggleConsent(consentType)}
                    className='privacy-policy-button'
                    data-testid={`privacy-tab-${consentType}`}
                  >
                    <span className='privacy-row-title'>{consent.config_value.title}</span>
                    <img
                      src={isExpanded ? collapseIcon : expandIcon}
                      alt={isExpanded ? 'Collapse' : 'Expand'}
                      className='icon'
                    />
                  </button>
                  {isExpanded && (
                    <div className='description'>
                      {formatContent(consent.config_value.content)}
                      {consent.config_value.version && (
                        <p className='consent-version'>
                          <small>
                            {t('Version')}: {consent.config_value.version}
                            {consent.config_value.effective_date &&
                              ` | ${t('Effective Date')}: ${new Date(
                                consent.config_value.effective_date,
                              ).toLocaleDateString()}`}
                          </small>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
};

export default PrivacyScreen;
